import React, { useEffect, useState } from "react";
import { Typography, Card, Tabs, message } from "antd";
import { trackFeatureUsage } from "../lib/posthog";
import { AdminNavBar } from "../components/AdminNavBar";
import styled from "@emotion/styled";
import { supabase } from "../lib/supabase";
import { ContainersList, MasterContainersList } from "../components/containers";
import type { Container as ContainerType } from "../types/booking";
import { useNavigate } from "react-router-dom";

const { Title } = Typography;

const Container = styled.div`
  padding: 24px;
`;

// Extended container type with info objects
export interface ContainerWithInfo extends ContainerType {
  transport_type_info?: {
    id: number;
    name: string;
    abbreviation: string;
  };
  origin_port_info?: {
    id: number;
    name: string;
    country: string;
  };
  destination_port_info?: {
    id: number;
    name: string;
    country: string;
  };
  // Use a different property name to avoid type conflict
  booking_list?: Array<{
    id: number;
    company_id: number | null;
    reference: string | null;
  }>;
  // Note: Unloading is now managed at the booking level, not container level
}

const ContainersAdmin: React.FC = () => {
  const [containers, setContainers] = useState<ContainerWithInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const navigate = useNavigate();

  // Fetch containers from Supabase
  const fetchContainers = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("containers")
        .select(
          `
          *,
          transport_type_info:booking_transport_types(*),
          origin_port_info:booking_origin_port(*),
          destination_port_info:booking_destination_port(*),
          booking_list:bookings(id, company_id, reference)
        `
        )
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      setContainers(data || []);
    } catch (error) {
      console.error("Error fetching containers:", error);
      message.error("Failed to load containers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Track page view
    trackFeatureUsage("containers_admin");

    fetchContainers();
  }, [refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const handleViewDetails = (containerId: number) => {
    // Navigate to the dedicated container details page
    navigate(`/admin/containers/${containerId}`);
  };

  const getFilteredContainers = () => {
    if (activeTab === "all") {
      return containers;
    }
    return containers.filter((container) => container.status === activeTab);
  };

  const items = [
    {
      key: "all",
      label: "All Containers",
      children: (
        <ContainersList
          containers={getFilteredContainers()}
          loading={loading}
          onRefresh={handleRefresh}
          onViewDetails={handleViewDetails}
        />
      ),
    },
    {
      key: "planned",
      label: "Planned",
      children: (
        <ContainersList
          containers={getFilteredContainers()}
          loading={loading}
          onRefresh={handleRefresh}
          onViewDetails={handleViewDetails}
        />
      ),
    },
    {
      key: "departed",
      label: "Departed",
      children: (
        <ContainersList
          containers={getFilteredContainers()}
          loading={loading}
          onRefresh={handleRefresh}
          onViewDetails={handleViewDetails}
        />
      ),
    },
    {
      key: "arrived",
      label: "Arrived",
      children: (
        <ContainersList
          containers={getFilteredContainers()}
          loading={loading}
          onRefresh={handleRefresh}
          onViewDetails={handleViewDetails}
        />
      ),
    },
    {
      key: "customs",
      label: "Customs",
      children: (
        <ContainersList
          containers={getFilteredContainers()}
          loading={loading}
          onRefresh={handleRefresh}
          onViewDetails={handleViewDetails}
        />
      ),
    },
    {
      key: "completed",
      label: "Completed",
      children: (
        <ContainersList
          containers={getFilteredContainers()}
          loading={loading}
          onRefresh={handleRefresh}
          onViewDetails={handleViewDetails}
        />
      ),
    },
    {
      key: "master-containers",
      label: "⭐ Master Containers",
      children: <MasterContainersList onRefresh={handleRefresh} />,
    },
  ];

  return (
    <Container>
      <AdminNavBar />

      <Title level={2}>Containers Management</Title>

      <Card style={{ marginTop: 24 }}>
        <Tabs activeKey={activeTab} onChange={handleTabChange} items={items} />
      </Card>
    </Container>
  );
};

export default ContainersAdmin;
